import {
	createContext,
	Dispatch,
	ReactNode,
	Reducer,
	useContext,
	useReducer,
} from "react";

import { MigrationStatus } from "../api/system-info";
import { SystemSettings } from "../api/system-settings";
import { User } from "../api/user";

type userColors = {
	[key: number]: string;
};

export type GlobalState = {
	loading?: boolean;
	currentUser?: User;
	userColors?: userColors;
	users?: User[];
	systemSettings?: SystemSettings;
	databaseHost?: string;
	databaseName?: string;
	gitBranch?: string;
	gitCommit?: string;
	gitCommitVersion?: string;
	/** ISO 8601 date of the deployed commit. */
	gitCommitDate?: string;
	/** "s1".."s5" when this is a staging instance. */
	stagingEnv?: string;
	/** Every hostname this instance answers on, aliases included. */
	stagingHostnames?: string[];
	/** Migration state of this system, superuser-only. */
	migrations?: MigrationStatus;
};

const initialGlobalState: GlobalState = {
	loading: true,
	currentUser: null,
	userColors: {},
	users: [],
};

export enum GlobalStateActions {
	SET_LOADING,
	UPDATE_USER_COLORS,
	UPDATE_USER,
	UPDATE_USERS,
	UPDATE_SYSTEM_SETTINGS,
	UPDATE_DATABASE_HOST,
}

export const GlobalStateReducer: Reducer<
	GlobalState,
	{ type: GlobalStateActions; data: GlobalState }
> = (state: GlobalState, action: { type: GlobalStateActions; data: GlobalState }) => {
	switch (action.type) {
		case GlobalStateActions.SET_LOADING:
			return { ...state, loading: action.data.loading };
		case GlobalStateActions.UPDATE_USER_COLORS:
			return { ...state, userColors: action.data.userColors };
		case GlobalStateActions.UPDATE_USER:
			return { ...state, currentUser: action.data.currentUser };
		case GlobalStateActions.UPDATE_USERS:
			return { ...state, users: action.data.users };
		case GlobalStateActions.UPDATE_SYSTEM_SETTINGS:
			return { ...state, systemSettings: action.data.systemSettings };
		case GlobalStateActions.UPDATE_DATABASE_HOST:
			return {
				...state,
				databaseHost: action.data.databaseHost,
				databaseName: action.data.databaseName,
				gitBranch: action.data.gitBranch,
				gitCommit: action.data.gitCommit,
				gitCommitVersion: action.data.gitCommitVersion,
				gitCommitDate: action.data.gitCommitDate,
				stagingEnv: action.data.stagingEnv,
				stagingHostnames: action.data.stagingHostnames,
				migrations: action.data.migrations,
			};
		default:
			throw new Error("Unknown action type");
	}
};

export const GlobalStateContext = createContext<GlobalState>(null);
export const GlobalStateDispatchContext =
	createContext<
		Dispatch<{
			type: GlobalStateActions;
			data: GlobalState;
		}>
	>(null);

export const GlobalStateProvider = ({ children }: { children: ReactNode }) => {
	const [globalState, dispatch] = useReducer(GlobalStateReducer, initialGlobalState);

	return (
		<GlobalStateContext.Provider value={globalState}>
			<GlobalStateDispatchContext.Provider value={dispatch}>
				{children}
			</GlobalStateDispatchContext.Provider>
		</GlobalStateContext.Provider>
	);
};

/**
 * Global state context
 * @returns Context containing global state
 */
export const useGlobalState = () => {
	return useContext(GlobalStateContext);
};

/**
 * Global state dispatcher context
 * @returns Context containing dispatcher function for global state (used to update state)
 */
export const useGlobalStateDispatch = () => {
	return useContext(GlobalStateDispatchContext);
};
